function getFocusableElements(container) {
  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
    )
  );
}

class SectionId {
  static #separator = "__";

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section id (e.g. 'template--22224696705326')
  static parseId(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[0];
  }

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section name (e.g. 'main')
  static parseSectionName(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[1];
  }

  // for a section id (e.g. 'template--22224696705326') and a section name (e.g. 'recommended-products'), return a qualified section id (e.g. 'template--22224696705326__recommended-products')
  static getIdForSection(sectionId, sectionName) {
    return `${sectionId}${SectionId.#separator}${sectionName}`;
  }
}

class HTMLUpdateUtility {
  /**
   * Used to swap an HTML node with a new node.
   * The new node is inserted as a previous sibling to the old node, the old node is hidden, and then the old node is removed.
   *
   * The function currently uses a double buffer approach, but this should be replaced by a view transition once it is more widely supported https://developer.mozilla.org/en-US/docs/Web/API/View_Transitions_API
   */
  static viewTransition(
    oldNode,
    newContent,
    preProcessCallbacks = [],
    postProcessCallbacks = []
  ) {
    preProcessCallbacks?.forEach((callback) => callback(newContent));

    const newNodeWrapper = document.createElement("div");
    HTMLUpdateUtility.setInnerHTML(newNodeWrapper, newContent.outerHTML);
    const newNode = newNodeWrapper.firstChild;

    // dedupe IDs
    const uniqueKey = Date.now();
    oldNode.querySelectorAll("[id], [form]").forEach((element) => {
      element.id && (element.id = `${element.id}-${uniqueKey}`);
      element.form &&
        element.setAttribute(
          "form",
          `${element.form.getAttribute("id")}-${uniqueKey}`
        );
    });

    oldNode.parentNode.insertBefore(newNode, oldNode);
    oldNode.style.display = "none";

    postProcessCallbacks?.forEach((callback) => callback(newNode));

    setTimeout(() => oldNode.remove(), 500);
  }

  // Sets inner HTML and reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
  static setInnerHTML(element, html) {
    element.innerHTML = html;
    element.querySelectorAll("script").forEach((oldScriptTag) => {
      const newScriptTag = document.createElement("script");
      Array.from(oldScriptTag.attributes).forEach((attribute) => {
        newScriptTag.setAttribute(attribute.name, attribute.value);
      });
      newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
      oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
    });
  }
}

document.querySelectorAll('[id^="Details-"] summary').forEach((summary) => {
  summary.setAttribute("role", "button");
  summary.setAttribute(
    "aria-expanded",
    summary.parentNode.hasAttribute("open")
  );

  if (summary.nextElementSibling.getAttribute("id")) {
    summary.setAttribute("aria-controls", summary.nextElementSibling.id);
  }

  summary.addEventListener("click", (event) => {
    event.currentTarget.setAttribute(
      "aria-expanded",
      !event.currentTarget.closest("details").hasAttribute("open")
    );
  });

  if (summary.closest("header-drawer, menu-drawer")) return;
  summary.parentElement.addEventListener("keyup", onKeyUpEscape);
});

const trapFocusHandlers = {};

function trapFocus(container, elementToFocus = container) {
  var elements = getFocusableElements(container);
  var first = elements[0];
  var last = elements[elements.length - 1];

  removeTrapFocus();

  trapFocusHandlers.focusin = (event) => {
    if (
      event.target !== container &&
      event.target !== last &&
      event.target !== first
    )
      return;

    document.addEventListener("keydown", trapFocusHandlers.keydown);
  };

  trapFocusHandlers.focusout = function () {
    document.removeEventListener("keydown", trapFocusHandlers.keydown);
  };

  trapFocusHandlers.keydown = function (event) {
    if (event.code.toUpperCase() !== "TAB") return; // If not TAB key
    // On the last focusable element and tab forward, focus the first element.
    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      first.focus();
    }

    //  On the first focusable element and tab backward, focus the last element.
    if (
      (event.target === container || event.target === first) &&
      event.shiftKey
    ) {
      event.preventDefault();
      last.focus();
    }
  };

  document.addEventListener("focusout", trapFocusHandlers.focusout);
  document.addEventListener("focusin", trapFocusHandlers.focusin);

  elementToFocus.focus();

  if (
    elementToFocus.tagName === "INPUT" &&
    ["search", "text", "email", "url"].includes(elementToFocus.type) &&
    elementToFocus.value
  ) {
    elementToFocus.setSelectionRange(0, elementToFocus.value.length);
  }
}

// Here run the querySelector to figure out if the browser supports :focus-visible or not and run code based on it.
try {
  document.querySelector(":focus-visible");
} catch (e) {
  focusVisiblePolyfill();
}

function focusVisiblePolyfill() {
  const navKeys = [
    "ARROWUP",
    "ARROWDOWN",
    "ARROWLEFT",
    "ARROWRIGHT",
    "TAB",
    "ENTER",
    "SPACE",
    "ESCAPE",
    "HOME",
    "END",
    "PAGEUP",
    "PAGEDOWN",
  ];
  let currentFocusedElement = null;
  let mouseClick = null;

  window.addEventListener("keydown", (event) => {
    if (navKeys.includes(event.code.toUpperCase())) {
      mouseClick = false;
    }
  });

  window.addEventListener("mousedown", (event) => {
    mouseClick = true;
  });

  window.addEventListener(
    "focus",
    () => {
      if (currentFocusedElement)
        currentFocusedElement.classList.remove("focused");

      if (mouseClick) return;

      currentFocusedElement = document.activeElement;
      currentFocusedElement.classList.add("focused");
    },
    true
  );
}

function pauseAllMedia() {
  document.querySelectorAll(".js-youtube").forEach((video) => {
    video.contentWindow.postMessage(
      '{"event":"command","func":"' + "pauseVideo" + '","args":""}',
      "*"
    );
  });
  document.querySelectorAll(".js-vimeo").forEach((video) => {
    video.contentWindow.postMessage('{"method":"pause"}', "*");
  });
  document.querySelectorAll("video").forEach((video) => video.pause());
  document.querySelectorAll("product-model").forEach((model) => {
    if (model.modelViewerUI) model.modelViewerUI.pause();
  });
}

function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener("focusin", trapFocusHandlers.focusin);
  document.removeEventListener("focusout", trapFocusHandlers.focusout);
  document.removeEventListener("keydown", trapFocusHandlers.keydown);

  if (elementToFocus) elementToFocus.focus();
}

function onKeyUpEscape(event) {
  if (event.code.toUpperCase() !== "ESCAPE") return;

  const openDetailsElement = event.target.closest("details[open]");
  if (!openDetailsElement) return;

  const summaryElement = openDetailsElement.querySelector("summary");
  openDetailsElement.removeAttribute("open");
  summaryElement.setAttribute("aria-expanded", false);
  summaryElement.focus();
}

class QuantityInput extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector("input");
    this.changeEvent = new Event("change", { bubbles: true });
    this.input.addEventListener("change", this.onInputChange.bind(this));
    this.querySelectorAll("button").forEach((button) =>
      button.addEventListener("click", this.onButtonClick.bind(this))
    );
  }

  quantityUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.validateQtyRules();
    this.quantityUpdateUnsubscriber = subscribe(
      PUB_SUB_EVENTS.quantityUpdate,
      this.validateQtyRules.bind(this)
    );
  }

  disconnectedCallback() {
    if (this.quantityUpdateUnsubscriber) {
      this.quantityUpdateUnsubscriber();
    }
  }

  onInputChange(event) {
    this.validateQtyRules();
    // Update royalty points when quantity is manually changed
    this.updateEarnPoints(event);
  }

  onButtonClick(event) {
    event.preventDefault();
    const previousValue = this.input.value;

    if (event.target.name === "plus") {
      if (
        parseInt(this.input.dataset.min) > parseInt(this.input.step) &&
        this.input.value == 0
      ) {
        this.input.value = this.input.dataset.min;
      } else {
        this.input.stepUp();
      }
    } else {
      this.input.stepDown();
    }
    if (previousValue !== this.input.value)
      this.input.dispatchEvent(this.changeEvent);

    if (
      this.input.dataset.min === previousValue &&
      event.target.name === "minus"
    ) {
      this.input.value = parseInt(this.input.min);
    }

    // Update royalty points when quantity changes
    this.updateEarnPoints(event);
  }

  updateEarnPoints(event) {
    const priceElement = document.querySelector(
      ".price__regular .price-item--regular"
    );
    const pointsElement = document.querySelector(
      ".login-to-earn-points strong"
    );
    const percentsPointsElement = document.querySelector(
      ".login-to-earn-points span b"
    );

    if (!priceElement || !pointsElement || !percentsPointsElement) return;
    // Check if the button has the stop-increase-point class (for cart items)
    if (
      event.target &&
      event.target.classList &&
      event.target.classList.contains("stop-increase-point")
    )
      return;

    // Remove the dollar symbol ('$') from the price and parse it as a float
    const priceText = priceElement.textContent.replace(/[^0-9.]/g, "");
    const priceRegular = parseFloat(priceText);

    if (isNaN(priceRegular)) return;

    const quantity = parseInt(this.input.value) || 1;
    const multi = priceRegular * quantity;
    const roundedMulti = Math.round(multi);
    const roundedDiv = roundedMulti / 100;

    percentsPointsElement.innerHTML = "$" + roundedDiv.toFixed(2);
    pointsElement.innerHTML = roundedMulti + " Points";
  }

  validateQtyRules() {
    const value = parseInt(this.input.value);
    if (this.input.min) {
      const buttonMinus = this.querySelector(".quantity__button[name='minus']");
      buttonMinus.classList.toggle(
        "disabled",
        parseInt(value) <= parseInt(this.input.min)
      );
    }
    if (this.input.max) {
      const max = parseInt(this.input.max);
      const buttonPlus = this.querySelector(".quantity__button[name='plus']");
      buttonPlus.classList.toggle("disabled", value >= max);
    }
  }
}

customElements.define("quantity-input", QuantityInput);

function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

function throttle(fn, delay) {
  let lastCall = 0;
  return function (...args) {
    const now = new Date().getTime();
    if (now - lastCall < delay) {
      return;
    }
    lastCall = now;
    return fn(...args);
  };
}

function fetchConfig(type = "json") {
  return {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: `application/${type}`,
    },
  };
}

/*
 * Shopify Common JS
 *
 */
if (typeof window.Shopify == "undefined") {
  window.Shopify = {};
}

Shopify.bind = function (fn, scope) {
  return function () {
    return fn.apply(scope, arguments);
  };
};

Shopify.setSelectorByValue = function (selector, value) {
  for (var i = 0, count = selector.options.length; i < count; i++) {
    var option = selector.options[i];
    if (value == option.value || value == option.innerHTML) {
      selector.selectedIndex = i;
      return i;
    }
  }
};

Shopify.addListener = function (target, eventName, callback) {
  target.addEventListener
    ? target.addEventListener(eventName, callback, false)
    : target.attachEvent("on" + eventName, callback);
};

Shopify.postLink = function (path, options) {
  options = options || {};
  var method = options["method"] || "post";
  var params = options["parameters"] || {};

  var form = document.createElement("form");
  form.setAttribute("method", method);
  form.setAttribute("action", path);

  for (var key in params) {
    var hiddenField = document.createElement("input");
    hiddenField.setAttribute("type", "hidden");
    hiddenField.setAttribute("name", key);
    hiddenField.setAttribute("value", params[key]);
    form.appendChild(hiddenField);
  }
  document.body.appendChild(form);
  form.submit();
  document.body.removeChild(form);
};

Shopify.CountryProvinceSelector = function (
  country_domid,
  province_domid,
  options
) {
  this.countryEl = document.getElementById(country_domid);
  this.provinceEl = document.getElementById(province_domid);
  this.provinceContainer = document.getElementById(
    options["hideElement"] || province_domid
  );

  Shopify.addListener(
    this.countryEl,
    "change",
    Shopify.bind(this.countryHandler, this)
  );

  this.initCountry();
  this.initProvince();
};

Shopify.CountryProvinceSelector.prototype = {
  initCountry: function () {
    var value = this.countryEl.getAttribute("data-default");
    Shopify.setSelectorByValue(this.countryEl, value);
    this.countryHandler();
  },

  initProvince: function () {
    var value = this.provinceEl.getAttribute("data-default");
    if (value && this.provinceEl.options.length > 0) {
      Shopify.setSelectorByValue(this.provinceEl, value);
    }
  },

  countryHandler: function (e) {
    var opt = this.countryEl.options[this.countryEl.selectedIndex];
    var raw = opt.getAttribute("data-provinces");
    var provinces = JSON.parse(raw);

    this.clearOptions(this.provinceEl);
    if (provinces && provinces.length == 0) {
      this.provinceContainer.style.display = "none";
    } else {
      for (var i = 0; i < provinces.length; i++) {
        var opt = document.createElement("option");
        opt.value = provinces[i][0];
        opt.innerHTML = provinces[i][1];
        this.provinceEl.appendChild(opt);
      }

      this.provinceContainer.style.display = "";
    }
  },

  clearOptions: function (selector) {
    while (selector.firstChild) {
      selector.removeChild(selector.firstChild);
    }
  },

  setOptions: function (selector, values) {
    for (var i = 0, count = values.length; i < values.length; i++) {
      var opt = document.createElement("option");
      opt.value = values[i];
      opt.innerHTML = values[i];
      selector.appendChild(opt);
    }
  },
};

class MenuDrawer extends HTMLElement {
  constructor() {
    super();

    this.mainDetailsToggle = this.querySelector("details");

    this.addEventListener("keyup", this.onKeyUp.bind(this));
    this.addEventListener("focusout", this.onFocusOut.bind(this));
    this.bindEvents();
  }

  bindEvents() {
    this.querySelectorAll("summary").forEach((summary) =>
      summary.addEventListener("click", this.onSummaryClick.bind(this))
    );
    this.querySelectorAll(
      "button:not(.localization-selector):not(.country-selector__close-button):not(.country-filter__reset-button)"
    ).forEach((button) =>
      button.addEventListener("click", this.onCloseButtonClick.bind(this))
    );
  }

  onKeyUp(event) {
    if (event.code.toUpperCase() !== "ESCAPE") return;

    const openDetailsElement = event.target.closest("details[open]");
    if (!openDetailsElement) return;

    openDetailsElement === this.mainDetailsToggle
      ? this.closeMenuDrawer(
          event,
          this.mainDetailsToggle.querySelector("summary")
        )
      : this.closeSubmenu(openDetailsElement);
  }

  onSummaryClick(event) {
    const summaryElement = event.currentTarget;
    const detailsElement = summaryElement.parentNode;
    const parentMenuElement = detailsElement.closest(".has-submenu");
    const isOpen = detailsElement.hasAttribute("open");
    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");

    function addTrapFocus() {
      trapFocus(
        summaryElement.nextElementSibling,
        detailsElement.querySelector("button")
      );
      summaryElement.nextElementSibling.removeEventListener(
        "transitionend",
        addTrapFocus
      );
    }

    if (detailsElement === this.mainDetailsToggle) {
      if (isOpen) event.preventDefault();
      isOpen
        ? this.closeMenuDrawer(event, summaryElement)
        : this.openMenuDrawer(summaryElement);

      if (window.matchMedia("(max-width: 990px)")) {
        document.documentElement.style.setProperty(
          "--viewport-height",
          `${window.innerHeight}px`
        );
      }
    } else {
      setTimeout(() => {
        detailsElement.classList.add("menu-opening");
        summaryElement.setAttribute("aria-expanded", true);
        parentMenuElement && parentMenuElement.classList.add("submenu-open");
        !reducedMotion || reducedMotion.matches
          ? addTrapFocus()
          : summaryElement.nextElementSibling.addEventListener(
              "transitionend",
              addTrapFocus
            );
      }, 100);
    }
  }

  openMenuDrawer(summaryElement) {
    setTimeout(() => {
      this.mainDetailsToggle.classList.add("menu-opening");
    });
    summaryElement.setAttribute("aria-expanded", true);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus = false) {
    if (event === undefined) return;

    this.mainDetailsToggle.classList.remove("menu-opening");
    this.mainDetailsToggle.querySelectorAll("details").forEach((details) => {
      details.removeAttribute("open");
      details.classList.remove("menu-opening");
    });
    this.mainDetailsToggle
      .querySelectorAll(".submenu-open")
      .forEach((submenu) => {
        submenu.classList.remove("submenu-open");
      });
    document.body.classList.remove(
      `overflow-hidden-${this.dataset.breakpoint}`
    );
    removeTrapFocus(elementToFocus);
    this.closeAnimation(this.mainDetailsToggle);

    if (event instanceof KeyboardEvent)
      elementToFocus?.setAttribute("aria-expanded", false);
  }

  onFocusOut() {
    setTimeout(() => {
      if (
        this.mainDetailsToggle.hasAttribute("open") &&
        !this.mainDetailsToggle.contains(document.activeElement)
      )
        this.closeMenuDrawer();
    });
  }

  onCloseButtonClick(event) {
    const detailsElement = event.currentTarget.closest("details");
    this.closeSubmenu(detailsElement);
  }

  closeSubmenu(detailsElement) {
    const parentMenuElement = detailsElement.closest(".submenu-open");
    parentMenuElement && parentMenuElement.classList.remove("submenu-open");
    detailsElement.classList.remove("menu-opening");
    detailsElement
      .querySelector("summary")
      .setAttribute("aria-expanded", false);
    removeTrapFocus(detailsElement.querySelector("summary"));
    this.closeAnimation(detailsElement);
  }

  closeAnimation(detailsElement) {
    let animationStart;

    const handleAnimation = (time) => {
      if (animationStart === undefined) {
        animationStart = time;
      }

      const elapsedTime = time - animationStart;

      if (elapsedTime < 40) {
        window.requestAnimationFrame(handleAnimation);
      } else {
        detailsElement.removeAttribute("open");
        if (detailsElement.closest("details[open]")) {
          trapFocus(
            detailsElement.closest("details[open]"),
            detailsElement.querySelector("summary")
          );
        }
      }
    };

    window.requestAnimationFrame(handleAnimation);
  }
}

customElements.define("menu-drawer", MenuDrawer);

class HeaderDrawer extends MenuDrawer {
  constructor() {
    super();
  }

  openMenuDrawer(summaryElement) {
    this.header = this.header || document.querySelector(".section-header");
    this.borderOffset =
      this.borderOffset ||
      this.closest(".header-wrapper").classList.contains(
        "header-wrapper--border-bottom"
      )
        ? 1
        : 0;
    document.documentElement.style.setProperty(
      "--header-bottom-position",
      `${parseInt(
        this.header.getBoundingClientRect().bottom - this.borderOffset
      )}px`
    );
    this.header.classList.add("menu-open");

    setTimeout(() => {
      this.mainDetailsToggle.classList.add("menu-opening");
    });

    summaryElement.setAttribute("aria-expanded", true);
    window.addEventListener("resize", this.onResize);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus) {
    if (!elementToFocus) return;
    super.closeMenuDrawer(event, elementToFocus);
    this.header.classList.remove("menu-open");
    window.removeEventListener("resize", this.onResize);
  }

  onResize = () => {
    this.header &&
      document.documentElement.style.setProperty(
        "--header-bottom-position",
        `${parseInt(
          this.header.getBoundingClientRect().bottom - this.borderOffset
        )}px`
      );
    document.documentElement.style.setProperty(
      "--viewport-height",
      `${window.innerHeight}px`
    );
  };
}

customElements.define("header-drawer", HeaderDrawer);

class ModalDialog extends HTMLElement {
  constructor() {
    super();
    this.querySelector('[id^="ModalClose-"]').addEventListener(
      "click",
      this.hide.bind(this, false)
    );
    this.addEventListener("keyup", (event) => {
      if (event.code.toUpperCase() === "ESCAPE") this.hide();
    });
    if (this.classList.contains("media-modal")) {
      this.addEventListener("pointerup", (event) => {
        if (
          event.pointerType === "mouse" &&
          !event.target.closest("deferred-media, product-model")
        )
          this.hide();
      });
    } else {
      this.addEventListener("click", (event) => {
        if (event.target === this) this.hide();
      });
    }
  }

  connectedCallback() {
    if (this.moved) return;
    this.moved = true;
    this.dataset.section = this.closest(".shopify-section").id.replace(
      "shopify-section-",
      ""
    );
    document.body.appendChild(this);
  }

  show(opener) {
    this.openedBy = opener;
    const popup = this.querySelector(".template-popup");
    document.body.classList.add("overflow-hidden");
    this.setAttribute("open", "");
    if (popup) popup.loadContent();
    trapFocus(this, this.querySelector('[role="dialog"]'));
    window.pauseAllMedia();
  }

  hide() {
    document.body.classList.remove("overflow-hidden");
    document.body.dispatchEvent(new CustomEvent("modalClosed"));
    this.removeAttribute("open");
    removeTrapFocus(this.openedBy);
    window.pauseAllMedia();
  }
}
customElements.define("modal-dialog", ModalDialog);

class BulkModal extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    const handleIntersection = (entries, observer) => {
      if (!entries[0].isIntersecting) return;
      observer.unobserve(this);
      if (this.innerHTML.trim() === "") {
        const productUrl = this.dataset.url.split("?")[0];
        fetch(`${productUrl}?section_id=bulk-quick-order-list`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(
              responseText,
              "text/html"
            );
            const sourceQty = html.querySelector(
              ".quick-order-list-container"
            ).parentNode;
            this.innerHTML = sourceQty.innerHTML;
          })
          .catch((e) => {
            console.error(e);
          });
      }
    };

    new IntersectionObserver(handleIntersection.bind(this)).observe(
      document.querySelector(
        `#QuickBulk-${this.dataset.productId}-${this.dataset.sectionId}`
      )
    );
  }
}

customElements.define("bulk-modal", BulkModal);

class ModalOpener extends HTMLElement {
  constructor() {
    super();

    const button = this.querySelector("button");

    if (!button) return;
    button.addEventListener("click", (event) => {
      event.preventDefault();
      event.stopPropagation();
      const modal = document.querySelector(this.getAttribute("data-modal"));
      if (modal) modal.show(button);
    });
  }
}
customElements.define("modal-opener", ModalOpener);

class DeferredMedia extends HTMLElement {
  constructor() {
    super();
    const poster = this.querySelector('[id^="Deferred-Poster-"]');
    if (!poster) return;
    poster.addEventListener("click", this.loadContent.bind(this));
  }

  loadContent(focus = true) {
    window.pauseAllMedia();
    if (!this.getAttribute("loaded")) {
      const content = document.createElement("div");
      content.appendChild(
        this.querySelector("template").content.firstElementChild.cloneNode(true)
      );

      this.setAttribute("loaded", true);
      const deferredElement = this.appendChild(
        content.querySelector("video, model-viewer, iframe")
      );
      if (focus) deferredElement.focus();
      if (
        deferredElement.nodeName == "VIDEO" &&
        deferredElement.getAttribute("autoplay")
      ) {
        // force autoplay for safari
        deferredElement.play();
      }

      // Workaround for safari iframe bug
      const formerStyle = deferredElement.getAttribute("style");
      deferredElement.setAttribute("style", "display: block;");
      window.setTimeout(() => {
        deferredElement.setAttribute("style", formerStyle);
      }, 0);
    }
  }
}

customElements.define("deferred-media", DeferredMedia);

class SliderComponent extends HTMLElement {
  constructor() {
    super();
    this.slider = this.querySelector('[id^="Slider-"]');
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.enableSliderLooping = false;
    this.currentPageElement = this.querySelector(".slider-counter--current");
    this.pageTotalElement = this.querySelector(".slider-counter--total");
    this.prevButton = this.querySelector('button[name="previous"]');
    this.nextButton = this.querySelector('button[name="next"]');

    if (!this.slider || !this.nextButton) return;

    this.initPages();
    const resizeObserver = new ResizeObserver((entries) => this.initPages());
    resizeObserver.observe(this.slider);

    this.slider.addEventListener("scroll", this.update.bind(this));
    this.prevButton.addEventListener("click", this.onButtonClick.bind(this));
    this.nextButton.addEventListener("click", this.onButtonClick.bind(this));
  }

  initPages() {
    this.sliderItemsToShow = Array.from(this.sliderItems).filter(
      (element) => element.clientWidth > 0
    );
    if (this.sliderItemsToShow.length < 2) return;
    this.sliderItemOffset =
      this.sliderItemsToShow[1].offsetLeft -
      this.sliderItemsToShow[0].offsetLeft;
    this.slidesPerPage = Math.floor(
      (this.slider.clientWidth - this.sliderItemsToShow[0].offsetLeft) /
        this.sliderItemOffset
    );
    this.totalPages = this.sliderItemsToShow.length - this.slidesPerPage + 1;
    this.update();
  }

  resetPages() {
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.initPages();
  }

  update() {
    // Temporarily prevents unneeded updates resulting from variant changes
    // This should be refactored as part of https://github.com/Shopify/dawn/issues/2057
    if (!this.slider || !this.nextButton) return;

    const previousPage = this.currentPage;
    this.currentPage =
      Math.round(this.slider.scrollLeft / this.sliderItemOffset) + 1;

    if (this.currentPageElement && this.pageTotalElement) {
      this.currentPageElement.textContent = this.currentPage;
      this.pageTotalElement.textContent = this.totalPages;
    }

    if (this.currentPage != previousPage) {
      this.dispatchEvent(
        new CustomEvent("slideChanged", {
          detail: {
            currentPage: this.currentPage,
            currentElement: this.sliderItemsToShow[this.currentPage - 1],
          },
        })
      );
    }

    if (this.enableSliderLooping) return;

    if (
      this.isSlideVisible(this.sliderItemsToShow[0]) &&
      this.slider.scrollLeft === 0
    ) {
      this.prevButton.setAttribute("disabled", "disabled");
    } else {
      this.prevButton.removeAttribute("disabled");
    }

    if (
      this.isSlideVisible(
        this.sliderItemsToShow[this.sliderItemsToShow.length - 1]
      )
    ) {
      this.nextButton.setAttribute("disabled", "disabled");
    } else {
      this.nextButton.removeAttribute("disabled");
    }
  }

  isSlideVisible(element, offset = 0) {
    const lastVisibleSlide =
      this.slider.clientWidth + this.slider.scrollLeft - offset;
    return (
      element.offsetLeft + element.clientWidth <= lastVisibleSlide &&
      element.offsetLeft >= this.slider.scrollLeft
    );
  }

  onButtonClick(event) {
    event.preventDefault();
    const step = event.currentTarget.dataset.step || 1;
    this.slideScrollPosition =
      event.currentTarget.name === "next"
        ? this.slider.scrollLeft + step * this.sliderItemOffset
        : this.slider.scrollLeft - step * this.sliderItemOffset;
    this.setSlidePosition(this.slideScrollPosition);
  }

  setSlidePosition(position) {
    this.slider.scrollTo({
      left: position,
    });
  }
}

customElements.define("slider-component", SliderComponent);

class SlideshowComponent extends SliderComponent {
  constructor() {
    super();
    this.sliderControlWrapper = this.querySelector(".slider-buttons");
    this.enableSliderLooping = true;

    if (!this.sliderControlWrapper) return;

    this.sliderFirstItemNode = this.slider.querySelector(".slideshow__slide");
    if (this.sliderItemsToShow.length > 0) this.currentPage = 1;

    this.announcementBarSlider = this.querySelector(".announcement-bar-slider");
    // Value below should match --duration-announcement-bar CSS value
    this.announcerBarAnimationDelay = this.announcementBarSlider ? 250 : 0;

    this.sliderControlLinksArray = Array.from(
      this.sliderControlWrapper.querySelectorAll(".slider-counter__link")
    );
    this.sliderControlLinksArray.forEach((link) =>
      link.addEventListener("click", this.linkToSlide.bind(this))
    );
    this.slider.addEventListener("scroll", this.setSlideVisibility.bind(this));
    this.setSlideVisibility();

    if (this.announcementBarSlider) {
      this.announcementBarArrowButtonWasClicked = false;

      this.reducedMotion = window.matchMedia(
        "(prefers-reduced-motion: reduce)"
      );
      this.reducedMotion.addEventListener("change", () => {
        if (this.slider.getAttribute("data-autoplay") === "true")
          this.setAutoPlay();
      });

      [this.prevButton, this.nextButton].forEach((button) => {
        button.addEventListener(
          "click",
          () => {
            this.announcementBarArrowButtonWasClicked = true;
          },
          { once: true }
        );
      });
    }

    if (this.slider.getAttribute("data-autoplay") === "true")
      this.setAutoPlay();
  }

  setAutoPlay() {
    this.autoplaySpeed = this.slider.dataset.speed * 1000;
    this.addEventListener("mouseover", this.focusInHandling.bind(this));
    this.addEventListener("mouseleave", this.focusOutHandling.bind(this));
    this.addEventListener("focusin", this.focusInHandling.bind(this));
    this.addEventListener("focusout", this.focusOutHandling.bind(this));

    if (this.querySelector(".slideshow__autoplay")) {
      this.sliderAutoplayButton = this.querySelector(".slideshow__autoplay");
      this.sliderAutoplayButton.addEventListener(
        "click",
        this.autoPlayToggle.bind(this)
      );
      this.autoplayButtonIsSetToPlay = true;
      this.play();
    } else {
      this.reducedMotion.matches || this.announcementBarArrowButtonWasClicked
        ? this.pause()
        : this.play();
    }
  }

  onButtonClick(event) {
    super.onButtonClick(event);
    this.wasClicked = true;

    const isFirstSlide = this.currentPage === 1;
    const isLastSlide = this.currentPage === this.sliderItemsToShow.length;

    if (!isFirstSlide && !isLastSlide) {
      this.applyAnimationToAnnouncementBar(event.currentTarget.name);
      return;
    }

    if (isFirstSlide && event.currentTarget.name === "previous") {
      this.slideScrollPosition =
        this.slider.scrollLeft +
        this.sliderFirstItemNode.clientWidth * this.sliderItemsToShow.length;
    } else if (isLastSlide && event.currentTarget.name === "next") {
      this.slideScrollPosition = 0;
    }

    this.setSlidePosition(this.slideScrollPosition);

    this.applyAnimationToAnnouncementBar(event.currentTarget.name);
  }

  setSlidePosition(position) {
    if (this.setPositionTimeout) clearTimeout(this.setPositionTimeout);
    this.setPositionTimeout = setTimeout(() => {
      this.slider.scrollTo({
        left: position,
      });
    }, this.announcerBarAnimationDelay);
  }

  update() {
    super.update();
    this.sliderControlButtons = this.querySelectorAll(".slider-counter__link");
    this.prevButton.removeAttribute("disabled");

    if (!this.sliderControlButtons.length) return;

    this.sliderControlButtons.forEach((link) => {
      link.classList.remove("slider-counter__link--active");
      link.removeAttribute("aria-current");
    });
    this.sliderControlButtons[this.currentPage - 1].classList.add(
      "slider-counter__link--active"
    );
    this.sliderControlButtons[this.currentPage - 1].setAttribute(
      "aria-current",
      true
    );
  }

  autoPlayToggle() {
    this.togglePlayButtonState(this.autoplayButtonIsSetToPlay);
    this.autoplayButtonIsSetToPlay ? this.pause() : this.play();
    this.autoplayButtonIsSetToPlay = !this.autoplayButtonIsSetToPlay;
  }

  focusOutHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton ||
        this.sliderAutoplayButton.contains(event.target);
      if (!this.autoplayButtonIsSetToPlay || focusedOnAutoplayButton) return;
      this.play();
    } else if (
      !this.reducedMotion.matches &&
      !this.announcementBarArrowButtonWasClicked
    ) {
      this.play();
    }
  }

  focusInHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton ||
        this.sliderAutoplayButton.contains(event.target);
      if (focusedOnAutoplayButton && this.autoplayButtonIsSetToPlay) {
        this.play();
      } else if (this.autoplayButtonIsSetToPlay) {
        this.pause();
      }
    } else if (this.announcementBarSlider.contains(event.target)) {
      this.pause();
    }
  }

  play() {
    this.slider.setAttribute("aria-live", "off");
    clearInterval(this.autoplay);
    this.autoplay = setInterval(
      this.autoRotateSlides.bind(this),
      this.autoplaySpeed
    );
  }

  pause() {
    this.slider.setAttribute("aria-live", "polite");
    clearInterval(this.autoplay);
  }

  togglePlayButtonState(pauseAutoplay) {
    if (pauseAutoplay) {
      this.sliderAutoplayButton.classList.add("slideshow__autoplay--paused");
      this.sliderAutoplayButton.setAttribute(
        "aria-label",
        window.accessibilityStrings.playSlideshow
      );
    } else {
      this.sliderAutoplayButton.classList.remove("slideshow__autoplay--paused");
      this.sliderAutoplayButton.setAttribute(
        "aria-label",
        window.accessibilityStrings.pauseSlideshow
      );
    }
  }

  autoRotateSlides() {
    const slideScrollPosition =
      this.currentPage === this.sliderItems.length
        ? 0
        : this.slider.scrollLeft + this.sliderItemOffset;

    this.setSlidePosition(slideScrollPosition);
    this.applyAnimationToAnnouncementBar();
  }

  setSlideVisibility(event) {
    this.sliderItemsToShow.forEach((item, index) => {
      const linkElements = item.querySelectorAll("a");
      if (index === this.currentPage - 1) {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.removeAttribute("tabindex");
          });
        item.setAttribute("aria-hidden", "false");
        item.removeAttribute("tabindex");
      } else {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.setAttribute("tabindex", "-1");
          });
        item.setAttribute("aria-hidden", "true");
        item.setAttribute("tabindex", "-1");
      }
    });
    this.wasClicked = false;
  }

  applyAnimationToAnnouncementBar(button = "next") {
    if (!this.announcementBarSlider) return;

    const itemsCount = this.sliderItems.length;
    const increment = button === "next" ? 1 : -1;

    const currentIndex = this.currentPage - 1;
    let nextIndex = (currentIndex + increment) % itemsCount;
    nextIndex = nextIndex === -1 ? itemsCount - 1 : nextIndex;

    const nextSlide = this.sliderItems[nextIndex];
    const currentSlide = this.sliderItems[currentIndex];

    const animationClassIn = "announcement-bar-slider--fade-in";
    const animationClassOut = "announcement-bar-slider--fade-out";

    const isFirstSlide = currentIndex === 0;
    const isLastSlide = currentIndex === itemsCount - 1;

    const shouldMoveNext =
      (button === "next" && !isLastSlide) ||
      (button === "previous" && isFirstSlide);
    const direction = shouldMoveNext ? "next" : "previous";

    currentSlide.classList.add(`${animationClassOut}-${direction}`);
    nextSlide.classList.add(`${animationClassIn}-${direction}`);

    setTimeout(() => {
      currentSlide.classList.remove(`${animationClassOut}-${direction}`);
      nextSlide.classList.remove(`${animationClassIn}-${direction}`);
    }, this.announcerBarAnimationDelay * 2);
  }

  linkToSlide(event) {
    event.preventDefault();
    const slideScrollPosition =
      this.slider.scrollLeft +
      this.sliderFirstItemNode.clientWidth *
        (this.sliderControlLinksArray.indexOf(event.currentTarget) +
          1 -
          this.currentPage);
    this.slider.scrollTo({
      left: slideScrollPosition,
    });
  }
}

customElements.define("slideshow-component", SlideshowComponent);

class VariantSelects extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.addEventListener("change", (event) => {
      const target = this.getInputForEventTarget(event.target);
      this.updateSelectionMetadata(event);

      const selectedValues = this.selectedOptionValues;

      // Спробуємо знайти і оновити варіант напряму (fallback для мобільних)
      this.updateVariantDirectly(selectedValues);

      publish(PUB_SUB_EVENTS.optionValueSelectionChange, {
        data: {
          event,
          target,
          selectedOptionValues: selectedValues,
        },
      });
    });
  }

  updateVariantDirectly(selectedOptionValues) {
    // Знайдемо product-info або ProductInfo div або product-gclid
    let productContainer = this.closest("product-info");
    let sectionId = null;

    if (productContainer) {
      sectionId = productContainer.dataset.section;
      console.log("[VariantSelects] Found product-info, sectionId:", sectionId);
    } else {
      // Спробуємо знайти div з id ProductInfo-*
      productContainer = this.closest('[id^="ProductInfo-"]');
      if (productContainer) {
        sectionId = productContainer.dataset.section;
        console.log(
          "[VariantSelects] Found ProductInfo div, sectionId:",
          sectionId
        );
      } else {
        // Спробуємо знайти через data-section на variant-selects
        sectionId = this.dataset.section;
        console.log(
          "[VariantSelects] Using variant-selects data-section, sectionId:",
          sectionId
        );
      }
    }

    if (!sectionId) {
      console.log("[VariantSelects] No sectionId found!");
      return;
    }

    console.log("[VariantSelects] Using sectionId for form search:", sectionId);

    // Отримаємо вибрані значення опцій безпосередньо з select елементів
    const selectedOptions = [];
    this.querySelectorAll("select").forEach((select) => {
      if (select.selectedOptions.length > 0) {
        selectedOptions.push(select.selectedOptions[0].value);
      }
    });

    // Додамо значення з radio buttons (якщо є)
    this.querySelectorAll("fieldset input:checked").forEach((input) => {
      selectedOptions.push(input.value);
    });

    if (selectedOptions.length === 0) {
      return;
    }

    // Отримаємо список варіантів з JSON script (не data-selected-variant)
    let variantScript = null;
    this.querySelectorAll('script[type="application/json"]').forEach(
      (script) => {
        if (!script.hasAttribute("data-selected-variant")) {
          variantScript = script;
        }
      }
    );

    if (!variantScript) {
      return;
    }

    let variants;
    try {
      variants = JSON.parse(variantScript.textContent);
    } catch (e) {
      return;
    }

    // Знайдемо варіант за значеннями опцій (option1, option2, option3)
    const matchingVariant = variants.find((variant) => {
      return (
        (selectedOptions.length >= 1
          ? variant.option1 === selectedOptions[0]
          : true) &&
        (selectedOptions.length >= 2
          ? variant.option2 === selectedOptions[1]
          : true) &&
        (selectedOptions.length >= 3
          ? variant.option3 === selectedOptions[2]
          : true)
      );
    });

    if (matchingVariant) {
      // Оновимо прихований input з ID варіанта
      const formSelectors = `#product-form-${sectionId}, #product-form-installment-${sectionId}`;
      console.log(
        "[VariantSelects] Looking for forms with selectors:",
        formSelectors
      );
      const forms = document.querySelectorAll(formSelectors);
      console.log("[VariantSelects] Found forms:", forms.length);

      forms.forEach((form, index) => {
        const input = form.querySelector('input[name="id"]');
        console.log(
          `[VariantSelects] Form ${index} - input found:`,
          !!input,
          "current value:",
          input?.value
        );
        if (input) {
          console.log(
            `[VariantSelects] Updating variant ID from ${input.value} to ${matchingVariant.id}`
          );
          input.value = matchingVariant.id;
          input.dispatchEvent(new Event("change", { bubbles: true }));
          console.log(`[VariantSelects] Variant ID updated to:`, input.value);
        } else {
          console.log(
            `[VariantSelects] No input[name="id"] found in form ${index}`
          );
        }
      });

      // Оновимо стан кнопки Add to Cart
      this.updateAddToCartButton(matchingVariant, sectionId);

      // Оновимо ціну, якщо можемо
      this.updatePrice(matchingVariant, sectionId);

      // Оновимо SKU, якщо є
      this.updateSKU(matchingVariant);

      // Зберігаємо поточний варіант для інших методів
      this.currentVariant = matchingVariant;
    } else {
      // Якщо варіант не знайдено - дізейблимо кнопку
      this.disableAddToCartButton(sectionId);
    }
  }

  updateAddToCartButton(variant, sectionId) {
    const submitButtons = document.querySelectorAll(
      `#product-form-${sectionId} [type="submit"], #product-form-installment-${sectionId} [type="submit"]`
    );

    console.log(
      "[VariantSelects] updateAddToCartButton called, variant:",
      variant,
      "sectionId:",
      sectionId
    );
    console.log("[VariantSelects] Found buttons:", submitButtons.length);

    submitButtons.forEach((button, index) => {
      const buttonText = button.querySelector("span");
      const soldOutMessage = button.querySelector(".sold-out-message");

      console.log(
        `[VariantSelects] Button ${index} - current disabled:`,
        button.hasAttribute("disabled"),
        "variant.available:",
        variant.available
      );

      if (!variant.available) {
        // Варіант недоступний
        button.setAttribute("disabled", "disabled");
        button.setAttribute("aria-disabled", "true");
        console.log(
          `[VariantSelects] Button ${index} - disabled (variant unavailable)`
        );

        if (buttonText && soldOutMessage) {
          buttonText.classList.add("hidden");
          soldOutMessage.classList.remove("hidden");
        } else if (buttonText) {
          buttonText.textContent = window.variantStrings?.soldOut || "Sold out";
        }
      } else {
        // Варіант доступний
        button.removeAttribute("disabled");
        button.removeAttribute("aria-disabled");
        console.log(
          `[VariantSelects] Button ${index} - enabled (variant available)`
        );

        if (buttonText && soldOutMessage) {
          buttonText.classList.remove("hidden");
          soldOutMessage.classList.add("hidden");
        } else if (buttonText) {
          buttonText.textContent =
            window.variantStrings?.addToCart || "Add to cart";
        }
      }
    });
  }

  disableAddToCartButton(sectionId) {
    const submitButtons = document.querySelectorAll(
      `#product-form-${sectionId} [type="submit"], #product-form-installment-${sectionId} [type="submit"]`
    );

    submitButtons.forEach((button) => {
      button.setAttribute("disabled", "disabled");
      button.setAttribute("aria-disabled", "true");

      const buttonText = button.querySelector("span");
      if (buttonText) {
        buttonText.textContent =
          window.variantStrings?.unavailable || "Unavailable";
      }
    });
  }

  updatePrice(variant, sectionId) {
    // Оновимо ціну якщо знайдемо price елемент
    const priceElement = document.querySelector(`#price-${sectionId}`);
    if (!priceElement) return;

    const priceRegular = priceElement.querySelector(".price-item--regular");
    const priceSale = priceElement.querySelector(".price-item--sale");

    if (variant.compare_at_price && variant.compare_at_price > variant.price) {
      // Є знижка
      if (priceRegular) {
        priceRegular.textContent = this.formatMoney(variant.compare_at_price);
      }
      if (priceSale) {
        priceSale.textContent = this.formatMoney(variant.price);
        priceSale.classList.remove("hidden");
      }
    } else {
      // Немає знижки
      if (priceRegular) {
        priceRegular.textContent = this.formatMoney(variant.price);
      }
      if (priceSale) {
        priceSale.classList.add("hidden");
      }
    }
  }

  formatMoney(cents) {
    // Базове форматування ціни (можна покращити)
    if (typeof Shopify !== "undefined" && Shopify.formatMoney) {
      return Shopify.formatMoney(cents);
    }
    return `$${(cents / 100).toFixed(2)}`;
  }

  updateSKU(variant) {
    // Оновимо SKU, якщо такий елемент є
    if (!variant.sku) return;

    const skuElements = document.querySelectorAll(
      ".js-update-sku, #update-sku, .variant--sku"
    );
    if (skuElements.length > 0) {
      skuElements.forEach((el) => {
        el.textContent = variant.sku;
      });
    }
  }

  updateSelectionMetadata({ target }) {
    const { value, tagName } = target;

    if (tagName === "SELECT" && target.selectedOptions.length) {
      const previouslySelected = Array.from(target.options).find((option) =>
        option.getAttribute("selected")
      );
      if (previouslySelected) {
        previouslySelected.removeAttribute("selected");
      }
      target.selectedOptions[0].setAttribute("selected", "selected");

      // Оновлюємо swatch для dropdown (якщо є)
      const swatchValue = target.selectedOptions[0].dataset.optionSwatchValue;
      const selectedDropdownSwatchValue = target
        .closest(".product-form__input")
        ?.querySelector("[data-selected-value] > .swatch");

      if (selectedDropdownSwatchValue) {
        if (swatchValue) {
          selectedDropdownSwatchValue.style.setProperty(
            "--swatch--background",
            swatchValue
          );
          selectedDropdownSwatchValue.classList.remove("swatch--unavailable");
        } else {
          selectedDropdownSwatchValue.style.setProperty(
            "--swatch--background",
            "unset"
          );
          selectedDropdownSwatchValue.classList.add("swatch--unavailable");
        }

        selectedDropdownSwatchValue.style.setProperty(
          "--swatch-focal-point",
          target.selectedOptions[0].dataset.optionSwatchFocalPoint || "unset"
        );
      }
    } else if (tagName === "INPUT" && target.type === "radio") {
      const selectedSwatchValue = target
        .closest(`.product-form__input`)
        ?.querySelector("[data-selected-value]");
      if (selectedSwatchValue) selectedSwatchValue.innerHTML = value;
    }

    if (this.currentVariant && this.currentVariant.sku !== undefined) {
      document
        .querySelectorAll(".js-update-sku")
        .forEach((el) => (el.innerHTML = this.currentVariant.sku));
    }
  }

  getInputForEventTarget(target) {
    return target.tagName === "SELECT" ? target.selectedOptions[0] : target;
  }

  get selectedOptionValues() {
    const values = [];

    // Для select елементів беремо selectedOptions - використовуємо або ID або значення
    this.querySelectorAll("select").forEach((select) => {
      if (select.selectedOptions.length > 0) {
        const selectedOption = select.selectedOptions[0];

        // Спробуємо отримати data-option-value-id
        const optionValueId = selectedOption.getAttribute(
          "data-option-value-id"
        );

        if (optionValueId) {
          values.push(optionValueId);
        } else {
          // Fallback: якщо немає ID, використовуємо просто значення
          // Це для сумісності з різними темами
          values.push(selectedOption.value);
        }
      }
    });

    // Для radio buttons беремо checked
    this.querySelectorAll("fieldset input:checked").forEach((input) => {
      const optionValueId = input.getAttribute("data-option-value-id");
      if (optionValueId) {
        values.push(optionValueId);
      } else {
        values.push(input.value);
      }
    });

    return values;
  }
}

customElements.define("variant-selects", VariantSelects);
class ProductRecommendations extends HTMLElement {
  observer = undefined;
  constructor() {
    super();
  }

  connectedCallback() {
    this.initializeRecommendations(this.dataset.productId);
  }

  initializeRecommendations(productId) {
    this.observer?.unobserve(this);
    this.observer = new IntersectionObserver(
      (entries, observer) => {
        if (!entries[0].isIntersecting) return;
        observer.unobserve(this);
        this.loadRecommendations(productId);
      },
      { rootMargin: "0px 0px 400px 0px" }
    );
    this.observer.observe(this);
  }

  loadRecommendations(productId) {
    fetch(
      `${this.dataset.url}&product_id=${productId}&section_id=${this.dataset.sectionId}`
    )
      .then((response) => response.text())
      .then((text) => {
        const html = document.createElement("div");
        html.innerHTML = text;
        const recommendations = html.querySelector("product-recommendations");

        if (recommendations?.innerHTML.trim().length) {
          this.innerHTML = recommendations.innerHTML;
        }

        if (
          !this.querySelector("slideshow-component") &&
          this.classList.contains("complementary-products")
        ) {
          this.remove();
        }

        if (html.querySelector(".grid__item")) {
          this.classList.add("product-recommendations--loaded");
        }

        if (typeof StampedFn !== "undefined" && StampedFn.loadBadges) {
          StampedFn.loadBadges();
        }
      })
      .catch((e) => {
        console.error(e);
      });
  }
}

customElements.define("product-recommendations", ProductRecommendations);

class AccountIcon extends HTMLElement {
  constructor() {
    super();

    this.icon = this.querySelector(".icon");
  }

  connectedCallback() {
    document.addEventListener(
      "storefront:signincompleted",
      this.handleStorefrontSignInCompleted.bind(this)
    );
  }

  handleStorefrontSignInCompleted(event) {
    if (event?.detail?.avatar) {
      this.icon?.replaceWith(event.detail.avatar.cloneNode());
    }
  }
}

customElements.define("account-icon", AccountIcon);

class BulkAdd extends HTMLElement {
  static ASYNC_REQUEST_DELAY = 250;

  constructor() {
    super();
    this.queue = [];
    this.setRequestStarted(false);
    this.ids = [];
  }

  startQueue(id, quantity) {
    this.queue.push({ id, quantity });

    const interval = setInterval(() => {
      if (this.queue.length > 0) {
        if (!this.requestStarted) {
          this.sendRequest(this.queue);
        }
      } else {
        clearInterval(interval);
      }
    }, BulkAdd.ASYNC_REQUEST_DELAY);
  }

  sendRequest(queue) {
    this.setRequestStarted(true);
    const items = {};
    queue.forEach((queueItem) => {
      items[parseInt(queueItem.id)] = queueItem.quantity;
    });
    this.queue = this.queue.filter(
      (queueElement) => !queue.includes(queueElement)
    );

    this.updateMultipleQty(items);
  }

  setRequestStarted(requestStarted) {
    this._requestStarted = requestStarted;
  }

  get requestStarted() {
    return this._requestStarted;
  }

  resetQuantityInput(id) {
    const input = this.querySelector(`#Quantity-${id}`);
    input.value = input.getAttribute("value");
    this.isEnterPressed = false;
  }

  setValidity(event, index, message) {
    event.target.setCustomValidity(message);
    event.target.reportValidity();
    this.resetQuantityInput(index);
    event.target.select();
  }

  validateQuantity(event) {
    const inputValue = parseInt(event.target.value);
    const index = event.target.dataset.index;

    if (inputValue < event.target.dataset.min) {
      this.setValidity(
        event,
        index,
        window.quickOrderListStrings.min_error.replace(
          "[min]",
          event.target.dataset.min
        )
      );
    } else if (inputValue > parseInt(event.target.max)) {
      this.setValidity(
        event,
        index,
        window.quickOrderListStrings.max_error.replace(
          "[max]",
          event.target.max
        )
      );
    } else if (inputValue % parseInt(event.target.step) != 0) {
      this.setValidity(
        event,
        index,
        window.quickOrderListStrings.step_error.replace(
          "[step]",
          event.target.step
        )
      );
    } else {
      event.target.setCustomValidity("");
      event.target.reportValidity();
      event.target.setAttribute("value", inputValue);
      this.startQueue(index, inputValue);
    }
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser()
      .parseFromString(html, "text/html")
      .querySelector(selector).innerHTML;
  }
}

if (!customElements.get("bulk-add")) {
  customElements.define("bulk-add", BulkAdd);
}

class CartPerformance {
  static #metric_prefix = "cart-performance";

  static createStartingMarker(benchmarkName) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`;
    return performance.mark(`${metricName}:start`);
  }

  static measureFromEvent(benchmarkName, event) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`;
    const startMarker = performance.mark(`${metricName}:start`, {
      startTime: event.timeStamp,
    });

    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(metricName, `${metricName}:start`, `${metricName}:end`);
  }

  static measureFromMarker(benchmarkName, startMarker) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`;
    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(metricName, startMarker.name, `${metricName}:end`);
  }

  static measure(benchmarkName, callback) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`;
    const startMarker = performance.mark(`${metricName}:start`);

    callback();

    const endMarker = performance.mark(`${metricName}:end`);
    performance.measure(metricName, `${metricName}:start`, `${metricName}:end`);
  }
}

/**
 * Wishlist Manager - централізована логіка роботи з wishlist
 */
class WishlistManager {
  static STORAGE_KEY = "wishlist-storage";

  /**
   * Отримати список товарів з wishlist
   * @returns {string[]} Масив handles товарів
   */
  static getWishlist() {
    try {
      const data = localStorage.getItem(this.STORAGE_KEY);
      if (!data || data === "[]" || data === "[null]") {
        return [];
      }
      const parsed = JSON.parse(data);
      return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
      console.error("WishlistManager: Error getting wishlist", e);
      return [];
    }
  }

  /**
   * Зберегти wishlist в localStorage
   * @param {string[]} wishlist - Масив handles товарів
   */
  static setWishlist(wishlist) {
    try {
      if (Array.isArray(wishlist) && wishlist.length > 0) {
        localStorage.setItem(this.STORAGE_KEY, JSON.stringify(wishlist));
      } else {
        localStorage.removeItem(this.STORAGE_KEY);
      }
      this.dispatchUpdateEvent();
    } catch (e) {
      console.error("WishlistManager: Error setting wishlist", e);
    }
  }

  /**
   * Додати товар в wishlist
   * @param {string} handle - Handle товару
   * @returns {boolean} Чи був товар доданий
   */
  static addProduct(handle) {
    if (!handle) return false;
    const wishlist = this.getWishlist();
    if (!wishlist.includes(handle)) {
      wishlist.push(handle);
      this.setWishlist(wishlist);
      return true;
    }
    return false;
  }

  /**
   * Видалити товар з wishlist
   * @param {string} handle - Handle товару
   * @returns {boolean} Чи був товар видалений
   */
  static removeProduct(handle) {
    if (!handle) return false;
    const wishlist = this.getWishlist();
    const index = wishlist.indexOf(handle);
    if (index !== -1) {
      wishlist.splice(index, 1);
      this.setWishlist(wishlist);
      return true;
    }
    return false;
  }

  /**
   * Перемкнути товар в wishlist (додати якщо немає, видалити якщо є)
   * @param {string} handle - Handle товару
   * @returns {boolean} Чи товар тепер в wishlist
   */
  static toggleProduct(handle) {
    if (!handle) return false;
    const wishlist = this.getWishlist();
    const index = wishlist.indexOf(handle);
    if (index !== -1) {
      wishlist.splice(index, 1);
      this.setWishlist(wishlist);
      return false;
    } else {
      wishlist.push(handle);
      this.setWishlist(wishlist);
      return true;
    }
  }

  /**
   * Перевірити чи товар в wishlist
   * @param {string} handle - Handle товару
   * @returns {boolean}
   */
  static hasProduct(handle) {
    if (!handle) return false;
    return this.getWishlist().includes(handle);
  }

  /**
   * Оновити UI всіх кнопок wishlist для товару
   * @param {string} handle - Handle товару
   * @param {boolean} isAdded - Чи товар в wishlist
   * @param {string} addText - Текст для додавання
   * @param {string} addedText - Текст для видалення
   */
  static updateButtons(handle, isAdded, addText, addedText) {
    const buttons = document.querySelectorAll(
      `[data-wishlist-handle="${handle}"]`
    );
    buttons.forEach((button) => {
      if (isAdded) {
        button.classList.add("is-added");
        button.setAttribute("title", addedText);
      } else {
        button.classList.remove("is-added");
        button.setAttribute("title", addText);
      }
    });
  }

  /**
   * Відправити custom event про оновлення wishlist
   */
  static dispatchUpdateEvent() {
    document.dispatchEvent(
      new CustomEvent("wishlist:updated", {
        detail: { wishlist: this.getWishlist() },
      })
    );
  }
}

/**
 * Кнопка wishlist для товару (для авторизованих користувачів)
 */
class ProductWishlistButton extends HTMLElement {
  constructor() {
    super();
    this.handle = null;
    this.addText = "";
    this.addedText = "";
    this.button = null;
  }

  connectedCallback() {
    this.button = this.querySelector("a[data-wishlist-handle]");
    if (!this.button) return;

    this.handle = this.button.dataset.wishlistHandle;
    this.addText = this.button.dataset.add || "Add to wishlist";
    this.addedText = this.button.dataset.added || "Added to wishlist";

    if (!this.handle) {
      console.error("ProductWishlistButton: Missing data-wishlist-handle");
      return;
    }

    this.button.addEventListener("click", this.handleClick.bind(this));
    this.updateState();

    // Слухаємо оновлення wishlist з інших джерел
    document.addEventListener("wishlist:updated", this.updateState.bind(this));
  }

  disconnectedCallback() {
    document.removeEventListener(
      "wishlist:updated",
      this.updateState.bind(this)
    );
  }

  handleClick(event) {
    event.preventDefault();
    event.stopPropagation();

    const isNowInWishlist = WishlistManager.toggleProduct(this.handle);
    WishlistManager.updateButtons(
      this.handle,
      isNowInWishlist,
      this.addText,
      this.addedText
    );
  }

  updateState() {
    const isAdded = WishlistManager.hasProduct(this.handle);
    if (isAdded) {
      this.button.classList.add("is-added");
      this.button.setAttribute("title", this.addedText);
    } else {
      this.button.classList.remove("is-added");
      this.button.setAttribute("title", this.addText);
    }
  }
}

customElements.define("product-wishlist", ProductWishlistButton);

/**
 * Кнопка wishlist для неавторизованих користувачів
 */
class WishlistLoginButton extends HTMLElement {
  constructor() {
    super();
    this.button = null;
  }

  connectedCallback() {
    this.button = this.querySelector("a");
    if (!this.button) return;

    this.button.addEventListener("click", this.handleClick.bind(this));
  }

  handleClick(event) {
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();

    const showPopup = () => {
      let popup = document.getElementById("popup-wishlist-js");

      // Якщо popup не знайдено, створити його динамічно
      if (!popup) {
        popup = this.createPopup();
      }

      if (popup) {
        popup.style.display = "flex";
        popup.style.visibility = "visible";
        document.body.style.overflow = "hidden";
        return true;
      }
      return false;
    };

    // Спробувати показати popup
    showPopup();
  }

  createPopup() {
    // Створити popup динамічно, якщо його немає в DOM
    const popup = document.createElement("div");
    popup.id = "popup-wishlist-js";
    popup.className = "wish-overlay";

    const wrapper = document.createElement("div");
    wrapper.className = "wish-popup-wrapper";

    const closeButton = document.createElement("button");
    closeButton.id = "close-wishlist-popup";
    closeButton.type = "button";
    closeButton.className = "quick-add-modal__toggle pop-close";
    closeButton.setAttribute("aria-label", "Close");
    closeButton.innerHTML = `
      <svg xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false" role="presentation" class="icon icon-close" fill="none" viewBox="0 0 18 17">
        <path d="M.865 15.978a.5.5 0 00.707.707l7.433-7.431 7.579 7.282a.501.501 0 00.846-.37.5.5 0 00-.153-.351L9.712 8.546l7.417-7.416a.5.5 0 10-.707-.708L8.991 7.853 1.413.573a.5.5 0 10-.693.72l7.563 7.268-7.418 7.417z" fill="currentColor"></path>
      </svg>
    `;

    const heading = document.createElement("h2");
    heading.className = "h1";
    heading.textContent = "Save to wishlist";

    const paragraph = document.createElement("p");
    paragraph.textContent =
      "Login or create an account to save this item to your wishlist.";

    const loginLink = document.createElement("a");
    loginLink.href = window.routes?.account_login_url || "/account/login";
    loginLink.className = "wish-singin-btn button button--primary";
    loginLink.textContent = "Login";

    const registerLink = document.createElement("a");
    registerLink.href =
      window.routes?.account_register_url || "/account/register";
    registerLink.className =
      "wish-account-btn wish-singin-btn button button--primary";
    registerLink.textContent = "Create Account";

    wrapper.appendChild(closeButton);
    wrapper.appendChild(heading);
    wrapper.appendChild(paragraph);
    wrapper.appendChild(loginLink);
    wrapper.appendChild(registerLink);
    popup.appendChild(wrapper);

    document.body.appendChild(popup);

    // Ініціалізувати popup після створення
    initWishlistPopup();

    return popup;
  }
}

customElements.define("wishlist-login", WishlistLoginButton);

/**
 * Ініціалізація popup для wishlist
 */
function initWishlistPopup() {
  const popup = document.getElementById("popup-wishlist-js");
  if (!popup) {
    // Якщо popup не знайдено, спробувати через невелику затримку (може рендериться пізніше)
    setTimeout(() => initWishlistPopup(), 100);
    return;
  }

  const closeButton = document.getElementById("close-wishlist-popup");

  const closePopup = () => {
    popup.style.display = "none";
    popup.style.visibility = "hidden";
    document.body.style.overflow = "";
  };

  if (closeButton) {
    closeButton.addEventListener("click", (e) => {
      e.preventDefault();
      e.stopPropagation();
      closePopup();
    });
  }

  popup.addEventListener("click", (event) => {
    if (
      event.target === popup ||
      event.target.classList.contains("wish-overlay")
    ) {
      closePopup();
    }
  });

  // Закрити popup при натисканні ESC
  document.addEventListener("keydown", (event) => {
    if (event.key === "Escape" && popup.style.display === "flex") {
      closePopup();
    }
  });
}

// Ініціалізувати popup після завантаження DOM
if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", initWishlistPopup);
} else {
  initWishlistPopup();
}

/**
 * Обробник кліків на картці товару - редірект на сторінку товару
 * Пропускає кліки на інтерактивні елементи (кнопки, посилання)
 */
class ProductCardClickHandler {
  static init() {
    document.addEventListener("click", (event) => {
      if (event.defaultPrevented) return;

      const cardWrapper = event.target.closest(".product-card-wrapper");
      if (!cardWrapper) return;

      const clickedElement = event.target;

      // Перевірка чи клік на інтерактивний елемент
      if (this.isInteractiveElement(clickedElement)) {
        return;
      }

      // Знайти посилання на товар
      const productLink = cardWrapper.querySelector(
        '.card__heading a[href*="/products/"], .full-unstyled-link[href*="/products/"]'
      );

      if (productLink && productLink.href) {
        event.preventDefault();
        window.location.href = productLink.href;
      }
    });
  }

  static isInteractiveElement(element) {
    // Перевірка кастомних елементів та класів (спочатку для правильної ієрархії)
    const interactiveSelectors = [
      ".wishlist-card",
      ".quick-add",
      "product-wishlist",
      "wishlist-login",
      "product-form",
      "modal-opener",
      "quick-add-modal",
      "quantity-popover",
      "[data-wishlist-handle]",
      ".login-wishlist-js",
    ];

    // Перевірка чи елемент або його батьківський елемент містить wishlist-login
    if (
      element.closest("wishlist-login") !== null ||
      element.matches("wishlist-login")
    ) {
      return true;
    }

    if (
      interactiveSelectors.some((selector) => {
        return element.closest(selector) !== null || element.matches(selector);
      })
    ) {
      return true;
    }

    // Перевірка інтерактивних елементів (але не посилання на товар)
    if (["BUTTON", "INPUT", "SELECT", "TEXTAREA"].includes(element.tagName)) {
      return true;
    }

    // Перевірка посилань - пропускаємо тільки якщо це не посилання на товар
    if (element.tagName === "A") {
      // Якщо посилання всередині wishlist-card - пропускаємо
      if (element.closest(".wishlist-card")) {
        return true;
      }
      // Інші посилання не пропускаємо (буде редірект на товар)
      return false;
    }

    return false;
  }
}

// Share Copy Link Custom Element
if (!customElements.get("share-copy-link")) {
  customElements.define(
    "share-copy-link",
    class ShareCopyLink extends HTMLElement {
      constructor() {
        super();
      }
    }
  );
}

// Ініціалізація обробника кліків на картках товару
document.addEventListener("DOMContentLoaded", () => {
  ProductCardClickHandler.init();

  // Social Share Drawer
  const shareButtons = document.querySelectorAll(".share-button-js");
  shareButtons.forEach((button) => {
    button.addEventListener("click", function () {
      const drawer = this.closest(".product-form__share").querySelector(
        ".share__drawer-js"
      );
      if (drawer) {
        if (drawer.style.display === "block") {
          drawer.style.display = "none";
        } else {
          // Закрити всі інші відкриті drawers
          document.querySelectorAll(".share__drawer-js").forEach((d) => {
            d.style.display = "none";
          });
          drawer.style.display = "block";
        }
      }
    });
  });

  // Copy product link
  document.querySelectorAll(".copy-link-js").forEach((copyBtn) => {
    copyBtn.addEventListener("click", function (event) {
      event.preventDefault();
      const shareContainer = this.closest(".share__drawer-js");
      if (shareContainer) {
        const input = shareContainer.querySelector(".get-url-js");
        if (input) {
          input.select();
          navigator.clipboard.writeText(input.value).then(() => {
            this.classList.add("has-active");
            setTimeout(() => {
              this.classList.remove("has-active");
            }, 2000);
          });
        }
      }
    });
  });

  // Close share drawer when clicking outside
  document.addEventListener("click", function (event) {
    if (!event.target.closest(".product-form__share")) {
      document.querySelectorAll(".share__drawer-js").forEach((drawer) => {
        drawer.style.display = "none";
      });
    }
  });
});
